import { collection, doc, getDoc, getDocs, query, where, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { User, UserRank, calculateRank } from '../types/user';

const USERS_COLLECTION = 'users';

// Fetch staked amount from creator.bid API
async function fetchStakedAmount(address: string): Promise<number> {
  try {
    const response = await fetch('https://creator.bid/api/agents/676ef7b23bdebce80077f770/members');
    const data = await response.json();
    
    // Find the member with matching address
    const member = data.members.find((m: any) => 
      m.address.toLowerCase() === address.toLowerCase()
    );
    
    // Return staked amount or 0 if not found
    return member ? parseFloat(member.amountLocked) : 0;
  } catch (error) {
    console.error('Error fetching staked amount:', error);
    return 0;
  }
}

export async function checkUsernameAvailability(username: string): Promise<boolean> {
  try {
    // Check both lowercase and exact match to ensure case-insensitive uniqueness
    const usersRef = collection(db, USERS_COLLECTION);
    const lowercaseQuery = query(usersRef, where('usernameLower', '==', username.toLowerCase()));
    const exactQuery = query(usersRef, where('username', '==', username));
    
    const [lowercaseSnapshot, exactSnapshot] = await Promise.all([
      getDocs(lowercaseQuery),
      getDocs(exactQuery)
    ]);
    
    return lowercaseSnapshot.empty && exactSnapshot.empty;
  } catch (error) {
    console.error('Error checking username availability:', error);
    throw error;
  }
}

export async function getUserByAddress(address: string): Promise<User | null> {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, address.toLowerCase()));
    
    if (userDoc.exists()) {
      return userDoc.data() as User;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
}

export async function createUser(address: string, username: string): Promise<void> {
  try {
    // Validate username format
    const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    if (!usernameRegex.test(username)) {
      throw new Error('Username must be 3-20 characters and can only contain letters, numbers, underscores, and hyphens');
    }
    
    // Check if username is available
    const isAvailable = await checkUsernameAvailability(username);
    if (!isAvailable) {
      throw new Error('Username is already taken');
    }

    // Fetch initial staked amount
    const stakedAmount = await fetchStakedAmount(address);
    
    // Create user document with initial rank and points
    const userData: User = {
      address: address.toLowerCase(),
      username: username,
      usernameLower: username.toLowerCase(), // Store lowercase version for case-insensitive checks
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      totalPoints: 0,
      rank: 'Ronin',
      stakedAmount: stakedAmount
    };
    
    await setDoc(doc(db, USERS_COLLECTION, address.toLowerCase()), userData);
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
}

export async function updateLastLogin(address: string): Promise<void> {
  try {
    // Fetch current staked amount
    const stakedAmount = await fetchStakedAmount(address);
    
    // Update last login and staked amount
    await setDoc(
      doc(db, USERS_COLLECTION, address.toLowerCase()),
      {
        lastLogin: new Date().toISOString(),
        stakedAmount: stakedAmount
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error updating last login:', error);
    throw error;
  }
}

// New function to update staked amount
export async function updateStakedAmount(address: string): Promise<number> {
  try {
    const stakedAmount = await fetchStakedAmount(address);
    
    await setDoc(
      doc(db, USERS_COLLECTION, address.toLowerCase()),
      {
        stakedAmount: stakedAmount,
        lastUpdated: new Date().toISOString()
      },
      { merge: true }
    );
    
    return stakedAmount;
  } catch (error) {
    console.error('Error updating staked amount:', error);
    throw error;
  }
}